import type { MyWorkItem, Priority } from './types';
import { deadlineKey, timeLeft, urgencyKey } from './timeLeft';
import type { WorkSource } from './workSource';
import { sourceOf } from './workSource';

/**
 * How somebody has chosen to look at their own list.
 *
 * The page always grouped by due date — overdue, today, this week — which answers "what is
 * late" and not "what do I do first". Ordering by priority-then-time-left answers that one,
 * and filters take out what is not today's concern. Every filter is a chip that can be taken
 * off again on its own, so narrowing the list is never a thing to undo all at once.
 */
export type MyWorkOrder = 'due' | 'urgency' | 'time_left';

export interface MyWorkView {
  order: MyWorkOrder;
  priorities: Priority[];
  sources: WorkSource[];
  /** Company id, or null for all of them. */
  company: string | null;
  overdueOnly: boolean;
  atRiskOnly: boolean;
}

export const DEFAULT_VIEW: MyWorkView = {
  order: 'due',
  priorities: [],
  sources: [],
  company: null,
  overdueOnly: false,
  atRiskOnly: false,
};

export const ORDER_LABELS: Record<MyWorkOrder, string> = {
  due: 'By due date',
  urgency: 'Priority, then time left',
  time_left: 'Least time left',
};

export function isNarrowed(view: MyWorkView): boolean {
  return (
    view.priorities.length > 0 || view.sources.length > 0 || view.company !== null || view.overdueOnly || view.atRiskOnly
  );
}

export function keep(item: MyWorkItem, view: MyWorkView, today: string): boolean {
  if (view.priorities.length > 0 && !view.priorities.includes(item.priority)) return false;
  if (view.sources.length > 0 && !view.sources.includes(sourceOf(item))) return false;
  if (view.company && item.company.id !== view.company) return false;
  if (view.overdueOnly && !timeLeft(item, today).late && !item.overdue) return false;
  // Breached counts as at risk: somebody asking "what is in trouble" means both.
  if (view.atRiskOnly && item.sla?.standing !== 'at_risk' && item.sla?.standing !== 'breached') return false;
  return true;
}

/** The list in the chosen order. `due` keeps the server's buckets, so it is not sorted here. */
export function ordered(items: MyWorkItem[], order: Exclude<MyWorkOrder, 'due'>, today: string): MyWorkItem[] {
  const key = (item: MyWorkItem) =>
    order === 'urgency' ? urgencyKey(item.priority, timeLeft(item, today)) : deadlineKey(timeLeft(item, today));
  return [...items].sort((a, b) => key(a) - key(b) || a.key.localeCompare(b.key));
}
